import React, { useEffect, useState } from "react";
import moment from "moment";
import { LuCopy, LuGift, LuTrophy, LuUsers, LuStar } from "react-icons/lu";
import {
  getMyReferralCode,
  getMyReferrals,
  getMyRewards,
  getLeaderboard,
} from "../../Services/referralService";
import { successToast } from "../../utils/toasts";

const tierColor = {
  Bronze: "bg-amber-100 text-amber-700",
  Silver: "bg-gray-200 text-gray-700",
  Gold: "bg-yellow-100 text-yellow-700",
  Platinum: "bg-indigo-100 text-indigo-700",
};

const statusColor = {
  pending: "bg-gray-100 text-gray-500",
  joined: "bg-blue-100 text-blue-700",
  converted: "bg-green-100 text-green-700",
};

const Referral = () => {
  const [code, setCode] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [rewards, setRewards] = useState({ stats: null, rewards: [] });
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, r, rw, lb] = await Promise.all([
          getMyReferralCode(),
          getMyReferrals(),
          getMyRewards(),
          getLeaderboard(),
        ]);
        setCode(c);
        setReferrals(r || []);
        setRewards(rw || { stats: null, rewards: [] });
        setLeaderboard(lb || []);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const shareCode = code?.code;
  const copy = () => {
    navigator.clipboard?.writeText(shareCode);
    successToast("Referral code copied!");
  };

  const stats = rewards.stats || { total_points: 0, total_cashback: 0, tier: "Bronze" };

  if (loading) return <div className="page-container p-6">Loading…</div>;

  return (
    <div className="page-container p-6">
      <div className="flex items-center gap-2 mb-6">
        <LuGift className="text-[#425F80]" size={26} />
        <h1 className="text-2xl font-bold text-gray-800">Refer & Earn</h1>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard icon={<LuStar />} label="Points" value={stats.total_points} />
        <StatCard
          icon={<LuGift />}
          label="Cashback"
          value={`₹${Number(stats.total_cashback).toLocaleString("en-IN")}`}
        />
        <StatCard icon={<LuUsers />} label="Referrals" value={referrals.length} />
        <div className="rounded-xl border border-gray-100 p-4 bg-white">
          <p className="text-xs text-gray-400 mb-1">Tier</p>
          <span
            className={`text-sm font-semibold px-2 py-0.5 rounded-full ${
              tierColor[stats.tier] || ""
            }`}
          >
            {stats.tier}
          </span>
        </div>
      </div>

      {/* Referral code */}
      <div className="rounded-2xl bg-gradient-to-r from-[#425F80] to-[#5b7ba3] text-white p-6 mb-8">
        <p className="text-sm opacity-90">Your referral code</p>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-3xl font-bold tracking-wider">{shareCode}</span>
          <button
            onClick={copy}
            className="flex items-center gap-1 bg-white/20 hover:bg-white/30 rounded-full px-3 py-1.5 text-sm"
          >
            <LuCopy size={14} /> Copy
          </button>
        </div>
        <p className="text-xs opacity-80 mt-2">
          Share with fellow advisors. Earn points & cashback when they join and make their first sale.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* My referrals */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-3">My Referrals</h2>
          {referrals.length === 0 ? (
            <p className="text-gray-400 text-sm">No referrals yet.</p>
          ) : (
            <div className="space-y-2">
              {referrals.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between bg-white rounded-xl border border-gray-100 px-4 py-3"
                >
                  <div>
                    <p className="font-medium text-gray-800">
                      {r.referred_name || "Invited advisor"}
                    </p>
                    <p className="text-xs text-gray-400">
                      {moment(r.created_at).format("DD MMM YYYY")}
                    </p>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${statusColor[r.status] || ""}`}
                  >
                    {r.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Leaderboard */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <LuTrophy className="text-amber-500" size={20} /> Leaderboard
          </h2>
          {leaderboard.length === 0 ? (
            <p className="text-gray-400 text-sm">No rankings yet.</p>
          ) : (
            <div className="space-y-2">
              {leaderboard.map((row) => (
                <div
                  key={row.advisor_id}
                  className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 px-4 py-3"
                >
                  <span
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                      row.rank <= 3 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {row.rank}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{row.name}</p>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full ${
                        tierColor[row.tier] || ""
                      }`}
                    >
                      {row.tier}
                    </span>
                  </div>
                  <span className="font-semibold text-gray-700">{row.total_points} pts</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value }) => (
  <div className="rounded-xl border border-gray-100 p-4 bg-white">
    <div className="flex items-center gap-2 text-gray-400 mb-1 text-xs">
      {icon} {label}
    </div>
    <p className="text-xl font-bold text-gray-800">{value}</p>
  </div>
);

export default Referral;
